import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import AppTheme from '../shared-theme/AppTheme';
import { CustomCard, CustomContainer } from '../shared-theme/BasicComponentTheme';
import McdcLogo from '../../assets/images/logo-mcdc.png';
import ClemsonLogo from '../../assets/images/logo-clemson.png';
import Link from "@mui/material/Link";
import { Divider } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuthManager } from "../../hooks/useAuthHook";
import { usePublicManager } from "../../hooks/usePublicHook";

interface DropdownOption {
  value: number;
  label: string;
}

export default function SignUp(props: { disableCustomTheme?: boolean }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [sectorOptions, setSectorOptions] = useState<DropdownOption[]>([]);
  const [countryOptions, setCountryOptions] = useState<DropdownOption[]>([]);
  const [roleOptions, setRoleOptions] = useState<DropdownOption[]>([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedSector, setSelectedSector] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [email, setEmail] = useState('');
  const [institution, setInstitution] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successPopupOpen, setSuccessPopupOpen] = useState(false);

  const navigate = useNavigate();

  const { useFetchCountries, useFetchRoles, useFetchSectors } = usePublicManager();
  const { data: countriesData, error: countriesError } = useFetchCountries();
  const { data: rolesData, error: rolesError } = useFetchRoles();
  const { data: sectorsData, error: sectorsError } = useFetchSectors();


  const { useSignUp } = useAuthManager();
  const { mutate: signUp } = useSignUp();

  const resetFields = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setInstitution('');
    setSelectedRole('');
    setSelectedSector('');
    setSelectedCountry('');
    setErrorMessage('');
  };

  const validateInputs = (): boolean => {
    if (!firstName.trim() || !lastName.trim()) {
      setErrorMessage('First Name and Last Name are required.');
      return false;
    }

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setErrorMessage('Please enter a valid email address.');
      return false;
    }

    if (!institution.trim()) {
      setErrorMessage('Institution is required.');
      return false;
    }

    if (!selectedCountry || !selectedRole || !selectedSector) {
      setErrorMessage('Please select Country, Role, and Sector.');
      return false;
    }

    if (!password || password.length < 6) {
      setErrorMessage('Password must be at least 6 characters long.');
      return false;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return false;
    }

    setErrorMessage('');
    return true;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateInputs()) {
      return;
    }

    const payload = {
      firstName,
      lastName,
      email,
      password,
      institution,
      roleID: parseInt(selectedRole),
      countryID: parseInt(selectedCountry),
      sectorID: parseInt(selectedSector),
      professionID: 1, // Adjust if you have a profession field
      identityProviderID: 1,
      appType: 2,
    };

    signUp(payload, {
      onSuccess: () => {
        setSuccessPopupOpen(true);
        resetFields();
      },
      onError: () => {
        setErrorMessage('We could not create your account. Please try again.');
      },
    });
  };

  useEffect(() => {
    if (sectorsError === null && sectorsData) {
      const sectors = sectorsData.data.map((sector) => ({
        value: sector.id,
        label: sector.label,
      }));
      setSectorOptions(sectors);
    }
    if (rolesError === null && rolesData) {
      const roles = rolesData.data.map((role) => ({
        value: role.id,
        label: role.label,
      }));
      setRoleOptions(roles);
    }

    if (countriesError === null && countriesData) {
      const countries = countriesData.data.map((country) => ({
        value: country.id,
        label: country.label,
      }));
      setCountryOptions(countries);
    }
  }, [countriesData, rolesData, sectorsData]);

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <CustomContainer direction="column" justifyContent="center">
        <CustomCard variant="outlined">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src={McdcLogo}
              alt="MCDC Logo"
              style={{ maxWidth: '120px', maxHeight: '120px', objectFit: 'contain' }}
            />
            <img
              src={ClemsonLogo}
              alt="Clemson Logo"
              style={{ maxWidth: '50px', maxHeight: '50px', objectFit: 'contain' }}
            />
          </Box>
          <Typography
            component="h1"
            variant="h4"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              fontSize: "clamp(1rem, 4vw, 2rem)",
            }}
          >
            Sign Up
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%', width: '100%' }}
          >
            {/* First Name & Last Name */}
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <FormLabel>First Name</FormLabel>
                <TextField
                  id="first-name"
                  placeholder="First Name"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </FormControl>
              <FormControl fullWidth>
                <FormLabel>Last Name</FormLabel>
                <TextField
                  id="last-name"
                  placeholder="Last Name"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </FormControl>
            </Stack>

            {/* Email Field */}
            <FormControl fullWidth>
              <FormLabel>Email</FormLabel>
              <TextField
                id="email"
                type="email"
                placeholder="your@email.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormControl>

            {/* Institution & Country */}
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <FormLabel>Institution</FormLabel>
                <TextField
                  id="institution"
                  placeholder="Your Institution"
                  required
                  value={institution}
                  onChange={(e) => setInstitution(e.target.value)}
                />
              </FormControl>
              <FormControl fullWidth>
                <FormLabel>Country</FormLabel>
                <Select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select Country
                  </MenuItem>
                  {countryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Sector & Role */}
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <FormLabel>Sector</FormLabel>
                <Select
                  value={selectedSector}
                  onChange={(e) => setSelectedSector(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select Sector
                  </MenuItem>
                  {sectorOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <FormLabel>Role</FormLabel>
                <Select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select Role
                  </MenuItem>
                  {roleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Password & Confirm Password */}
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <FormLabel>Password</FormLabel>
                <TextField
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
              <FormControl fullWidth>
                <FormLabel>Confirm Password</FormLabel>
                <TextField
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                          {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            </Stack>

            {/* Error Message */}
            {errorMessage && (
              <Typography color="error" textAlign="center">
                {errorMessage}
              </Typography>
            )}

            <Button type="submit" variant="contained" fullWidth className="mt-2">
              Sign Up
            </Button>
          </Box>
          <Divider></Divider>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography>
              Already have an account?{' '}
              <Link onClick={() => navigate("/signin")} sx={{ cursor: 'pointer' }}>
                Sign In
              </Link>
            </Typography>
          </Box>
        </CustomCard>
      </CustomContainer>

      {/* Success Popup */}
      <Dialog open={successPopupOpen} onClose={() => setSuccessPopupOpen(false)}>
        <DialogTitle>Sign Up Successful</DialogTitle>
        <DialogContent>
          <Typography>
            Please check your inbox for a verification email. If you do not see it, kindly check your spam or junk folder, as it may have been filtered there by mistake. Follow the instructions in the email to activate your account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessPopupOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </AppTheme>
  );
}
